/* eslint-disable no-param-reassign */
import _get from 'lodash/get';
import _isFunction from 'lodash/isFunction';
import _join from 'lodash/join';
import _split from 'lodash/split';
import _map from 'lodash/map';
import _replace from 'lodash/replace';
import _pick from 'lodash/pick';
import _unset from 'lodash/unset';
import EventEmitter from 'eventemitter3';
import produce from 'immer';
import LoginService from '../services/loginService';
import { sendException } from '../middlewares/errorTracker';
import TEnvReader from '../readers/Env';
import { logApiErrorInfoToPuppeteer, logApiDebugInfoToPuppeteer } from './puppeteerLoggingUtils';
export var IpErrorEventEmitter = new EventEmitter();
export var IP_ERROR = 'IP_ERROR';
var numbersRegex = /\d/;

var parameterizeUrlPart = function parameterizeUrlPart(urlPart, index) {
  if (!urlPart) return urlPart;
  if (!numbersRegex.test(urlPart)) return urlPart;
  return "_param".concat(index + 1);
};

var parameterizeURL = function parameterizeURL(url, baseURL) {
  var urlPath = _replace(url, baseURL, '');

  return _join(_map(_split(urlPath, '/'), parameterizeUrlPart), '/');
};

export var getErrorMessage = function getErrorMessage(err) {
  var defaultMessage = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : __('Something went wrong.');

  var message = _get(err, 'data.errorDetails.displayMessage') || _get(err, 'data.errorDetails.debugMessage');

  return message || _get(err, 'message', defaultMessage);
};
export var getErrorCode = function getErrorCode(err) {
  return _get(err, 'data.errorDetails.errorCode');
};
export var notifyErrorAtPuppeteer = function notifyErrorAtPuppeteer(errorPayload) {
  if (_isFunction(window.logMsgAtPuppeteer)) {
    window.logMsgAtPuppeteer("".concat(JSON.stringify(errorPayload)));

    if (_isFunction(window.generatePdf)) {
      window.generatePdf({
        errorOccured: true,
        errorPayload: errorPayload
      });
    }
  }
};
export var getStrippedErrorResponse = function getStrippedErrorResponse(e) {
  try {
    var strippedErr = produce(e, function (draft) {
      _unset(draft, ['response', 'config', 'headers', 'tekion-api-token']);
    });
    return _get(strippedErr, 'response');
  } catch (err) {
    // Immer might fail for circular references, https://immerjs.github.io/immer/pitfalls/
    var response = _get(e, 'response');

    var pickedResponse = _pick(response, ['headers.requesturl', 'headers.traceid', 'config.params', 'status', 'data']);

    return pickedResponse;
  }
};
export var apiInterceptor = function apiInterceptor(client) {
  client.interceptors.request.use(function (config) {
    config.metadata = {
      startTime: new Date()
    };
    return config;
  });
  client.interceptors.response.use(function (response) {
    response.config.metadata.endTime = new Date();
    response.duration = response.config.metadata.endTime - response.config.metadata.startTime;
    logApiDebugInfoToPuppeteer(response);
    return response;
  }, function (error) {
    var statusCode = _get(error, 'response.status');

    var ipValid = _get(error, 'response.headers.ipvalid');

    var errorCode = _get(error, 'response.data.errorDetails.errorCode');

    if (statusCode === 403 && ipValid === 'false') {
      return IpErrorEventEmitter.emit(IP_ERROR);
    }

    if (statusCode === 403 && errorCode === 'IS1001') {
      LoginService.newLogOut();
    }

    if (statusCode === 401) {
      try {
        notifyErrorAtPuppeteer({
          message: "Unauthorized headers! - ".concat(JSON.stringify(error.response))
        }); // eslint-disable-next-line no-empty
      } catch (e) {}

      LoginService.newLogOut();
    }

    var responseURL = _get(error, 'response.config.url');

    var baseURL = _get(error, 'response.config.baseURL');

    var strippedErrorResponse = getStrippedErrorResponse(error);

    if (_isFunction(window.slack)) {
      try {
        window.slack("ERROR FROM INTERCEPTOR: ".concat(responseURL, " - ").concat(baseURL, ": ").concat(JSON.stringify(strippedErrorResponse))); // eslint-disable-next-line no-empty
      } catch (err) {}
    }

    var parameterizedURL = parameterizeURL(responseURL, baseURL);
    var apiErrorContext = {
      payload: _get(error, 'response.config.data'),
      params: _get(error, 'response.config.params')
    };
    var isExportMode = TEnvReader.isExportMode();

    if (!isExportMode) {
      var sentryAddl = {
        context: apiErrorContext,
        groupingParams: [parameterizedURL, TEnvReader.buildEnv(process.env)]
      };
      sendException(new Error("".concat(statusCode, " at ").concat(responseURL)), sentryAddl);

      if (window.__OPENREPLAY__ && _isFunction(window.__OPENREPLAY__.handleError)) {
        var errorMessage = "".concat(statusCode, " at ").concat(responseURL);

        window.__OPENREPLAY__.handleError(new Error(errorMessage));
      }
    } else {
      logApiErrorInfoToPuppeteer(strippedErrorResponse);
    }

    return Promise.reject(error.response);
  });
};
export var getValidationResultParams = function getValidationResultParams(_ref) {
  var validationFlag = _ref.validationFlag,
      errorMsg = _ref.errorMsg;
  return validationFlag ? {
    isValid: true
  } : {
    isValid: false,
    message: errorMsg
  };
};